/**
 * What a profile says about an entity's place in the network.
 *
 * Pure: given the network and an entity, it works out who that entity is
 * connected to and who to go through to reach them. The same analytics as the
 * Network workspace, read from one entity's point of view.
 */

import type { NetworkAnalysis } from '../graphAnalytics';
import { KIND_LABEL, currentRole, formatSpan, roleLine } from '../network/networkModel';
import type { NetworkGraph, NetworkLink, NetworkNode } from '../types';

/** One connection of the entity whose profile this is */
export interface ProfileTie {
  link: NetworkLink;
  other: NetworkNode;
  /** Why these two are tied, in the words a reader needs */
  reason: string;
  /** True while the tie still holds */
  current: boolean;
}

export interface ProfileRoute {
  id: string;
  node: NetworkNode;
  /** 0–100, how good a way in this is */
  score: number;
  /** What makes them a way in */
  why: string;
  /** How they are tied to the entity */
  reason: string;
  /** Nobody else connects to the entity: everything goes through this one */
  only: boolean;
  influencePct: number;
}

export interface ProfileNetworkView {
  node: NetworkNode | null;
  ties: ProfileTie[];
  routes: ProfileRoute[];
  /** Ties that have ended, kept apart from the ones that hold */
  former: ProfileTie[];
  /** Entities two steps away, most connected first */
  secondDegree: number;
  /** One sentence about where this entity sits */
  standing: string | null;
  influencePct: number | null;
  /** They are the only way into somewhere else, which is worth knowing */
  isGatekeeper: boolean;
}

/** How strong a tie is on its own terms, 0–100 */
export function tieStrength(link: NetworkLink): number {
  const base = Math.min(5, Math.max(1, link.weight)) * 16; // 16–80
  const kindBonus =
    link.kind === 'relationship' ? 20 : link.kind === 'employment' ? 12 : link.kind === 'hierarchy' ? 10 : 0;
  return Math.min(100, base + kindBonus);
}

/** What ties these two, said plainly */
export function tieReason(link: NetworkLink, other: NetworkNode, subject: NetworkNode): string {
  const span = formatSpan(link.startDate, link.endDate, link.isCurrent);
  const dated = (text: string) => (span ? `${text} · ${span}` : text);
  switch (link.kind) {
    case 'employment':
      return dated(
        subject.type === 'stakeholder'
          ? link.label || 'Works there'
          : `${link.label || 'Works here'}`,
      );
    case 'hierarchy':
      // These always run parent → child, so the direction says which is which
      return link.source === other.id ? 'The body it sits under' : 'Sits under this one';
    case 'co_affiliation':
      return dated(link.label ? `Together at ${link.label}` : 'Worked together');
    case 'relationship':
    default:
      return dated(link.label || link.category || KIND_LABEL.relationship);
  }
}

/**
 * The entity's own corner of the network.
 *
 * Ties that still hold come first; ones that have ended are kept separately,
 * because who somebody used to be connected to reads differently from who
 * they are connected to now.
 */
export function egoView(
  graph: NetworkGraph | null,
  analysis: NetworkAnalysis | null,
  id: string | undefined,
): ProfileNetworkView {
  const empty: ProfileNetworkView = {
    node: null,
    ties: [],
    routes: [],
    former: [],
    secondDegree: 0,
    standing: null,
    influencePct: null,
    isGatekeeper: false,
  };
  if (!graph || !id) return empty;
  const byId = new Map(graph.nodes.map((node) => [node.id, node]));
  const node = byId.get(id);
  if (!node) return empty;

  const ties: ProfileTie[] = [];
  const former: ProfileTie[] = [];
  const neighbours = new Set<string>();
  for (const link of graph.links) {
    if (link.source !== id && link.target !== id) continue;
    const otherId = link.source === id ? link.target : link.source;
    const other = byId.get(otherId);
    if (!other) continue;
    const tie: ProfileTie = {
      link,
      other,
      reason: tieReason(link, other, node),
      current: link.isCurrent !== false,
    };
    if (tie.current) {
      ties.push(tie);
      neighbours.add(otherId);
    } else {
      former.push(tie);
    }
  }

  const metrics = analysis?.metrics.get(id) ?? null;
  const strongest = (tie: ProfileTie) => tieStrength(tie.link);
  ties.sort((a, b) => strongest(b) - strongest(a) || a.other.label.localeCompare(b.other.label));
  former.sort((a, b) => (b.link.endDate ?? '').localeCompare(a.link.endDate ?? ''));

  // Who to go through: their contacts, weighed by how much those contacts
  // carry in the network and how strong the tie to them is
  const routes: ProfileRoute[] = ties
    .map((tie) => {
      const theirs = analysis?.metrics.get(tie.other.id);
      const influencePct = theirs?.pct.influence ?? 0;
      // A person can make an introduction; an organization is a door rather
      // than a hand, so it ranks behind an equally well-placed person
      const person = tie.other.type === 'stakeholder' ? 1 : 0.85;
      const score = Math.round((influencePct * 0.6 + tieStrength(tie.link) * 0.4) * person);
      return {
        id: tie.other.id,
        node: tie.other,
        score,
        why: whyRoute(theirs?.pct.influence ?? 0, theirs?.degree ?? 0),
        reason: tie.reason,
        only: false,
        influencePct,
      };
    })
    .sort((a, b) => b.score - a.score || a.node.label.localeCompare(b.node.label));

  // Somebody tied twice — a recorded contact who is also a colleague — is one
  // way in, not two: the strongest tie speaks for them
  const best = new Map<string, ProfileRoute>();
  for (const route of routes) {
    const held = best.get(route.id);
    if (!held) best.set(route.id, route);
    else if (route.score > held.score) best.set(route.id, route);
  }
  const unique = Array.from(best.values()).sort(
    (a, b) => b.score - a.score || a.node.label.localeCompare(b.node.label),
  );

  if (unique.length === 1) unique[0].only = true;

  let secondDegree = 0;
  if (analysis) {
    const reached = new Set<string>();
    for (const otherId of Array.from(neighbours)) {
      const index = analysis.graph.index.get(otherId);
      if (index === undefined) continue;
      for (const next of analysis.graph.nbrs[index]) {
        const nextId = analysis.graph.ids[next];
        if (nextId !== id && !neighbours.has(nextId)) reached.add(nextId);
      }
    }
    secondDegree = reached.size;
  }

  return {
    node,
    ties,
    routes: unique,
    former,
    secondDegree,
    standing: metrics ? standingLine(node, metrics.pct.influence, ties.length) : null,
    influencePct: metrics?.pct.influence ?? null,
    isGatekeeper: !!metrics?.isArticulation,
  };
}

function whyRoute(influencePct: number, degree: number): string {
  if (influencePct >= 90) return 'One of the best-connected people in the directory';
  if (influencePct >= 70) return 'Well connected';
  if (degree >= 8) return `Tied to ${degree} others`;
  return 'Direct contact';
}

/** "Tied to 14 entities · better connected than 92% of the directory" */
export function standingLine(node: NetworkNode, influencePct: number, ties: number): string {
  const who = node.type === 'organization' ? 'This organization' : 'They';
  if (!ties) return `${who} has no ties recorded yet`;
  const reach =
    influencePct >= 60
      ? ` · better connected than ${Math.round(influencePct)}% of the directory`
      : '';
  return `${who === 'They' ? 'Tied to' : 'Tied to'} ${ties} ${ties === 1 ? 'entity' : 'entities'}${reach}`;
}

/** The label under a name in a list: their post, or what kind of body it is */
export const subtitleOf = (node: NetworkNode): string => {
  const role = currentRole(node);
  if (role) return [role.title, role.organizationName].filter(Boolean).join(' · ');
  return roleLine(node);
};

/* ------------------------------------------------------------------ */
/* The small map                                                       */
/* ------------------------------------------------------------------ */

export interface EgoPoint {
  id: string;
  node: NetworkNode;
  angle: number;
  x: number;
  y: number;
  radius: number;
  link: NetworkLink;
}

/**
 * Places the strongest ties around the entity.
 *
 * People and organizations are kept on separate arcs so the picture reads at
 * a glance: the organizations they belong to on one side, the people they are
 * tied to on the other.
 */
export function egoLayout(ties: ProfileTie[], limit = 14, ring = 108): EgoPoint[] {
  const shown = ties.slice(0, limit);
  if (!shown.length) return [];
  const people = shown.filter((tie) => tie.other.type === 'stakeholder');
  const orgs = shown.filter((tie) => tie.other.type === 'organization');
  const points: EgoPoint[] = [];
  const place = (group: ProfileTie[], from: number, to: number) => {
    const step = group.length > 1 ? (to - from) / (group.length - 1) : 0;
    group.forEach((tie, index) => {
      const angle = group.length === 1 ? (from + to) / 2 : from + step * index;
      const radius = ring - Math.min(18, tieStrength(tie.link) / 6);
      points.push({
        id: tie.other.id,
        node: tie.other,
        angle,
        x: Math.cos(angle) * radius,
        y: Math.sin(angle) * radius,
        radius,
        link: tie.link,
      });
    });
  };
  // Organizations on the left half, people on the right
  if (orgs.length) place(orgs, Math.PI * 0.62, Math.PI * 1.38);
  if (people.length) place(people, -Math.PI * 0.38, Math.PI * 0.38);
  return points;
}
